// Block J (UI-22, 2026-10-11): the week, on the v2 kit (src/ui) with tasks.css. Day cards in a grid
// on tablet and desktop, stacked on a phone; every task can be dragged to another day OR moved with
// "Move to…" (addendum 7), and both go through utils/taskMove.js. Preferences, team events, synced
// calendar events and the failure banner are as they were.
import React, { useEffect, useState, useCallback } from "react";
import {
  getAllTasksForDate,
  isExternalCalendarTask,
  toLocalDateStr,
} from "../utils/calendarUtils";
import useExternalCalendarEvents, { filterEventsForDate, fmtSyncedTime, PROVIDER_LABEL, PROVIDER_GLYPH } from "../hooks/useExternalCalendarEvents";
import { api } from "../api/client";
import { onCalendarPrefs } from "../utils/calendarPrefsEvent";
import { formatTaskClock, formatTaskDay } from "../utils/taskTime";   // fix(task-dates)
import { PRIORITY_RANK } from "../utils/priorityColors";
import { isCalendarEventTask, taskDisplayTitle } from "../utils/calendarTask";   // Fresh-eyes 19
import { moveTaskToDay } from "../utils/taskMove";
import { Banner, Button, Chip, MoveToSheet, v } from "../ui";
import { personTint } from "./Team/teamPeople";
import { TasksStandalone } from "./TasksShell";
import "./tasks.css";

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtTime(timeStr) {
  return formatTaskClock(timeStr) || null;   // fix(task-dates): the shared formatter
}

function sortByPriority(list) {
  return [...list].sort((a, b) => {
    const pa = PRIORITY_RANK[a.priority] ?? 2;
    const pb = PRIORITY_RANK[b.priority] ?? 2;
    if (pa !== pb) return pa - pb;
    return (a.time || "").localeCompare(b.time || "");
  });
}

const priorityKey = (p) => {
  const k = String(p || "medium").toLowerCase();
  return ["low", "medium", "high"].includes(k) ? k : "medium";
};

// One task in a day: open it, drag it, or "Move to…".
function DayTask({ task, date, onClickTask, onDragTask, onMoveTo }) {
  return (
    <div
      className="tk-day__task"
      draggable
      onDragStart={e => {
        e.stopPropagation();
        onDragTask && onDragTask(task, date);
        if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
      }}
    >
      {/* Fresh-eyes 19: a calendar event is a meeting, never a task with a priority. */}
      <span className={`tk-dot tk-dot--${isCalendarEventTask(task) ? 'meeting' : priorityKey(task.priority)}`} aria-hidden="true" />
      <button type="button" className="tk-day__open" onClick={e => { e.stopPropagation(); onClickTask(task); }}>
        <span className="tk-day__title">{taskDisplayTitle(task)}</span>
      </button>
      <div className="tk-day__foot">
        <span className="tk-small">{task.time ? fmtTime(task.time) : ''}</span>
        <Button size="sm" variant="tertiary" aria-label={`Move "${task.title}" to…`} onClick={e => { e.stopPropagation(); onMoveTo(task); }}>Move</Button>
      </div>
    </div>
  );
}

// ── Day column ────────────────────────────────────────────────────────────────
const TASK_LIMIT = 4;

function DayColumn({
  date, allTasks, isToday, isWeekend,
  onClickColumn, onClickMore, onClickTask,
  onDragTask, onDropTask, onDragOverDay, isDragOver, teamEvents, teamMembers,
  bufferBefore = 0, bufferAfter = 0,
  syncedEvents = [], onMoveTo,
}) {
  const regularTasks = sortByPriority(
    allTasks.filter(t => !isExternalCalendarTask(t) && !t.completed && t.source !== 'buffer')
  );
  const bufferTasks = allTasks.filter(t => t.source === 'buffer');
  const visibleTasks = regularTasks.slice(0, TASK_LIMIT);
  const hiddenCount = Math.max(0, regularTasks.length - TASK_LIMIT);
  // Block D8: buffers must appear in time order ALONGSIDE tasks, not stacked at the
  // top of the day. Merge tasks + buffers into one list ordered by time-of-day.
  const timeKey = (t) => (t.time && /^\d{1,2}:\d{2}/.test(t.time))
    ? t.time.padStart(5, '0')
    : (t.datetime ? String(t.datetime).slice(11, 16) : '99:99');
  const dayItems = [...visibleTasks, ...bufferTasks].sort((a, b) => timeKey(a).localeCompare(timeKey(b)));
  // syncedEvents replaces the old client-side calEvents — they come from the backend calendar_events table
  const calEvents = syncedEvents;

  const fullDayName = date.toLocaleDateString([], { weekday: "long" });
  const dateStr = toLocalDateStr(date);

  // Team events for this day
  const dayTeamEvents = (teamEvents || []).filter(e => e.datetime?.slice(0, 10) === dateStr);

  return (
    <section
      className={`tk-day${isToday ? " tk-day--today" : ""}${isWeekend ? " tk-day--weekend" : ""}${isDragOver ? " tk-day--over" : ""}`}
      data-date={dateStr}
      aria-label={`${fullDayName} ${formatTaskDay(date)}`}
      onDragOver={e => { e.preventDefault(); onDragOverDay && onDragOverDay(dateStr); }}
      onDrop={e => { e.preventDefault(); e.stopPropagation(); onDropTask && onDropTask(date); }}
    >
      <button type="button" className="tk-day__head" onClick={onClickColumn}>
        <span className="tk-day__name">{fullDayName}</span>
        <span className="tk-day__date">{formatTaskDay(date)}{isToday ? " · Today" : ""}</span>
      </button>

      <div className="tk-day__body">
        {regularTasks.length === 0 && calEvents.length === 0 && dayTeamEvents.length === 0 && bufferTasks.length === 0 && (
          <p className="tk-day__empty">{isDragOver ? "Drop here to move" : "No tasks"}</p>
        )}

        {/* Aidey tasks + buffers, interleaved in time order (Block D8) */}
        {dayItems.map(task => task.source === 'buffer' ? (
          <div key={task.id} className="tk-day__buffer">
            ⏸ Buffer{task.datetime ? ` · ${fmtTime(String(task.datetime).slice(11, 16))}` : ''}
          </div>
        ) : (
          <DayTask key={task.id} task={task} date={date} onClickTask={onClickTask} onDragTask={onDragTask} onMoveTo={onMoveTo} />
        ))}

        {hiddenCount > 0 && (
          <Button size="sm" variant="tertiary" className="tk-more" onClick={e => { e.stopPropagation(); onClickMore(); }}>
            +{hiddenCount} more
          </Button>
        )}

        {/* Synced external calendar events (read-only) */}
        {calEvents.length > 0 && (
          <>
            <p className="tk-day__label">Calendar</p>
            {calEvents.slice(0, 4).map((ev, i) => {
              const providerLabel = PROVIDER_LABEL[ev.provider] || 'Calendar';
              const timeStr = ev.startTime ? fmtSyncedTime(ev.startTime) : null;
              return (
                <div key={ev.id || i} className="tk-event" title={`${ev.title || 'Event'}\n${timeStr || ''}\nSynced from ${providerLabel}`}>
                  <span aria-hidden="true">{PROVIDER_GLYPH[ev.provider] || '📅'}</span>
                  <span className="tk-bar__title">
                    <span className="tk-event__title">{ev.title || 'Untitled event'}</span>
                    {timeStr && <span className="tk-small">{timeStr}</span>}
                  </span>
                </div>
              );
            })}
          </>
        )}

        {/* Team events */}
        {dayTeamEvents.length > 0 && (
          <>
            <p className="tk-day__label">Team</p>
            {dayTeamEvents.slice(0, 4).map((ev, i) => (
              <div key={ev.id || i} className="tk-day__team" style={{ '--tk-member': v(personTint(ev.userId)) }}>
                <strong>{ev.title}</strong>
                {ev.datetime && <span className="tk-small" style={{ display: 'block' }}>{fmtTime(ev.datetime)}</span>}
              </div>
            ))}
          </>
        )}
      </div>
    </section>
  );
}

// ── Main WeekView ─────────────────────────────────────────────────────────────
function WeekView({
  tasks,
  // THE WEEK HAD NO FAILURE STATE ON EITHER VIEWPORT (2026-10-04). TasksPage rendered it
  // without the flag App.js already computes, so a failed load showed seven empty days —
  // the same defect as the task list, one tab along, and invisible to the fix that threaded
  // the flag into the lists.
  tasksLoadFailed = false,
  today,
  setSelectedWeekDate,
  setShowWeekModal,
  setViewTask,
  onNavigateDay,
  onAddTask,
  embedded = false,
}) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" && window.matchMedia('(max-width: 767px)').matches
  );
  const [teamView, setTeamView] = useState(false);
  const [teamEvents, setTeamEvents] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [hiddenMembers, setHiddenMembers] = useState(new Set());
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [moving, setMoving] = useState(null);   // the task whose "Move to…" sheet is open
  const [calPrefs, setCalPrefs] = useState({
    weekStartsOn: 1,
    showWeekends: true,
    bufferBefore: 0,
    bufferAfter: 0,
  });

  useEffect(() => {
    const fromServer = d => setCalPrefs({
      weekStartsOn: d.weekStartsOn ?? 1,
      showWeekends: d.showWeekends ?? true,
      bufferBefore: d.bufferBefore ?? 0,
      bufferAfter: d.bufferAfter ?? 0,
    });
    api('/api/calendar/preferences')
      .then(r => r.json())
      .then(fromServer)
      .catch(() => {});
    // Decision 9d: a change saved in Settings applies at once.
    return onCalendarPrefs(fromServer);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobileViewport(e.matches);
    setIsMobileViewport(mq.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  // Start of week based on user preference (0 = Sunday, 1 = Monday)
  const todayWeekday = today.getDay();
  const baseMonday = new Date(today);
  baseMonday.setDate(today.getDate() - ((todayWeekday - calPrefs.weekStartsOn + 7) % 7));
  baseMonday.setHours(0, 0, 0, 0);

  const monday = new Date(baseMonday);
  monday.setDate(baseMonday.getDate() + weekOffset * 7);

  const weekdays = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
  const saturday = new Date(monday); saturday.setDate(monday.getDate() + 5);
  const sunday   = new Date(monday); sunday.setDate(monday.getDate() + 6);
  const weekDays = [...weekdays, saturday, sunday];
  const displayDays = calPrefs.showWeekends ? weekDays : weekdays;

  // Fetch synced external calendar events for the visible week
  const weekStart = monday.toISOString();
  const weekEnd   = new Date(sunday.getTime() + 86400000).toISOString();
  const { events: syncedEvents } = useExternalCalendarEvents(weekStart, weekEnd);

  // Fetch team events when team view is on
  useEffect(() => {
    if (!teamView) { setTeamEvents([]); setTeamMembers([]); return; }
    // Local days, not UTC instants: the route reads the first ten characters as a wall-clock day
    // (UI-17), and monday.toISOString() is Sunday evening in UTC for a UK summer week.
    const day = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    api(`/api/calendar/team?start=${day(monday)}&end=${day(sunday)}`)
      .then(r => r.json())
      .then(d => {
        if (Array.isArray(d.events)) setTeamEvents(d.events);
        if (Array.isArray(d.members)) setTeamMembers(d.members);
      })
      .catch(() => {});
  }, [teamView, weekOffset]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtRange = () => {
    const startDay   = monday.getDate();
    const endDay     = sunday.getDate();
    const startMonth = monday.toLocaleDateString([], { month: "short" });
    const endMonth   = sunday.toLocaleDateString([], { month: "short" });
    const year       = sunday.getFullYear();
    return startMonth === endMonth
      ? `${startDay} to ${endDay} ${endMonth} ${year}`
      : `${startDay} ${startMonth} to ${endDay} ${endMonth} ${year}`;
  };

  function getDayTasks(date) {
    const localStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    // External events are passed separately via syncedEvents — no external args needed here
    return getAllTasksForDate(tasks, [], localStr, [], []);
  }

  function getDaySyncedEvents(date) {
    const localStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return filterEventsForDate(syncedEvents, localStr);
  }

  function handleDayClick(date) {
    if (onNavigateDay) {
      onNavigateDay(date);
    } else {
      setSelectedWeekDate(date);
      setShowWeekModal(true);
    }
  }

  // ── Drag to reschedule, and its tap route: both are moveTaskToDay ─────────
  const handleDragTask = useCallback((task, fromDate) => {
    setDraggedTask({ task, fromDate });
    setDragOverDate(null);
  }, []);

  const handleDropTask = useCallback(async (toDate) => {
    if (!draggedTask) return;
    const { task } = draggedTask;
    setDraggedTask(null);
    setDragOverDate(null);
    await moveTaskToDay(task, toDate);
  }, [draggedTask]);

  const visibleTeamEvents = teamView
    ? teamEvents.filter(e => !hiddenMembers.has(e.userId))
    : [];

  function colProps(date) {
    const dateStr = toLocalDateStr(date);
    const jsDay = date.getDay();
    return {
      date,
      allTasks: getDayTasks(date),
      syncedEvents: getDaySyncedEvents(date),
      isToday: date.toDateString() === today.toDateString(),
      isWeekend: jsDay === 0 || jsDay === 6,
      isDragOver: !!draggedTask && dragOverDate === dateStr,
      teamEvents: visibleTeamEvents,
      teamMembers,
      onClickColumn: () => handleDayClick(date),
      onClickMore: () => { setSelectedWeekDate(date); setShowWeekModal(true); },
      onClickTask: setViewTask,
      onDragTask: handleDragTask,
      onDropTask: handleDropTask,
      onDragOverDay: (d) => { if (d !== dragOverDate) setDragOverDate(d); },
      onMoveTo: setMoving,
      bufferBefore: calPrefs.bufferBefore,
      bufferAfter: calPrefs.bufferAfter,
    };
  }

  const heading = weekOffset === 0 ? "This week" : weekOffset > 0 ? "A future week" : "A past week";
  const movingFrom = moving?.datetime ? toLocalDateStr(new Date(moving.datetime)) : moving?.date;

  return (
    <TasksStandalone embedded={embedded} title="This week" subline={`Week of ${fmtRange()}`} onAddTask={onAddTask}>
      {/* ── Header ── */}
      <div className="tk-bar">
        <div className="tk-bar__title">
          <h2>{heading}</h2>
        </div>
        <div className="tk-bar__end">
          <Chip selected={teamView} onClick={() => setTeamView(v => !v)}>👥 {teamView ? "Team: on" : "Team"}</Chip>
        </div>
      </div>

      {/* Navigation row */}
      <div className="tk-weeknav">
        <Button variant="tertiary" onClick={() => setWeekOffset(o => o - 1)} aria-label="Previous week">
          {isMobileViewport ? "←" : "← Previous week"}
        </Button>
        <div className="tk-weeknav__range">
          <span>Week of {fmtRange()}</span>
          {weekOffset !== 0 && (
            <Button size="sm" variant="secondary" onClick={() => setWeekOffset(0)}>Back to this week</Button>
          )}
        </div>
        <Button variant="tertiary" onClick={() => setWeekOffset(o => o + 1)} aria-label="Next week">
          {isMobileViewport ? "→" : "Next week →"}
        </Button>
      </div>

      {/* Team legend + visibility chips */}
      {teamView && teamMembers.length > 0 && (
        <div className="tk-members" role="group" aria-label="Team members shown">
          {teamMembers.map((m) => {
            const hidden = hiddenMembers.has(m.id);
            return (
              <Chip key={m.id} selected={!hidden}
                onClick={() => setHiddenMembers(prev => { const n = new Set(prev); hidden ? n.delete(m.id) : n.add(m.id); return n; })}>
                <span className="tk-swatch" style={{ '--tk-member': v(personTint(m.id)) }} aria-hidden="true" /> {m.name || m.email}
              </Chip>
            );
          })}
        </div>
      )}

      {/* ABOVE BOTH VIEWPORTS. Seven empty day cards and seven empty day columns look the
          same as a clear week, and a week she believes is clear is a week she does not work.
          A banner rather than a replacement, so the week is still navigable. */}
      {tasksLoadFailed && (
        <div data-testid="week-load-failed">
          <Banner tone="problem" action={<Button size="sm" variant="secondary" onClick={() => window.dispatchEvent(new Event('aidey:tasksUpdated'))}>Try again</Button>}>
            Your tasks could not be loaded, this is not a clear week. Check your connection and try again.
          </Banner>
        </div>
      )}

      {isMobileViewport ? (
        <div className="tk-stack">
          {displayDays.map((date) => {
            const allDay = getDayTasks(date);
            const dayTasks = sortByPriority(
              allDay.filter(t => !isExternalCalendarTask(t) && !t.completed && t.source !== 'buffer')
            );
            // ── "NO TASKS" WAS A LIE ON A DAY HOLDING ONLY CALENDAR EVENTS ────
            //
            // Carla, 2026-10-04: "Fix the phone's false 'No tasks' when a day holds only
            // synced calendar events. I would have believed my week was clear."
            //
            // `dayTasks` above deliberately strips external calendar events, completed
            // tasks and buffers — which is right for the LIST it renders, and wrong as the
            // test for emptiness. The desktop column has always required all four
            // collections to be empty before it says the same thing (DayColumn); the
            // phone asked only about regular tasks. So a day with three meetings synced
            // from her calendar read as a clear day on the device she actually uses.
            const daySynced = getDaySyncedEvents(date);
            const dayTeam = (visibleTeamEvents || []).filter(e => e.datetime?.slice(0, 10) === toLocalDateStr(date));
            const dayIsEmpty = dayTasks.length === 0 && daySynced.length === 0 && dayTeam.length === 0;
            const dayName = date.toLocaleDateString([], { weekday: "long" });
            const isToday = date.toDateString() === today.toDateString();
            const jsDay = date.getDay();
            const visibleTasks = dayTasks.slice(0, 3);
            const hiddenCount = Math.max(0, dayTasks.length - 3);
            return (
              <section key={toLocalDateStr(date)} data-date={toLocalDateStr(date)}
                className={`tk-day${isToday ? " tk-day--today" : ""}${jsDay === 0 || jsDay === 6 ? " tk-day--weekend" : ""}`}
                onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); handleDropTask(date); }}>
                <button type="button" className="tk-day__head" onClick={() => handleDayClick(date)}>
                  <span className="tk-day__name">{dayName}</span>
                  <span className="tk-day__date">{formatTaskDay(date)}{isToday ? " · Today" : ""}</span>
                </button>
                <div className="tk-day__body" style={dayIsEmpty ? { minHeight: 0 } : undefined}>
                  {dayIsEmpty ? (
                    <p className="tk-day__empty">No tasks</p>
                  ) : dayTasks.length === 0 ? (
                    /* Not empty, but nothing of HERS: the day is taken up by synced calendar
                       or team events. Saying how many stops the card reading as blank while
                       also not pretending they are her tasks. */
                    <p data-testid="week-day-events-only" className="tk-muted">
                      {daySynced.length + dayTeam.length} calendar {daySynced.length + dayTeam.length === 1 ? 'event' : 'events'} · tap the day to open
                    </p>
                  ) : (
                    visibleTasks.map((task) => (
                      <DayTask key={task.id} task={task} date={date} onClickTask={setViewTask} onDragTask={handleDragTask} onMoveTo={setMoving} />
                    ))
                  )}
                  {hiddenCount > 0 && (
                    <Button size="sm" variant="tertiary" className="tk-more" onClick={() => handleDayClick(date)}>+{hiddenCount} more</Button>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      ) : (
        <div className="tk-week">
          {displayDays.map(date => <DayColumn key={toLocalDateStr(date)} {...colProps(date)} />)}
        </div>
      )}

      {/* The tap route for the drag (addendum 7): the days of this week. */}
      <MoveToSheet
        open={!!moving}
        onClose={() => setMoving(null)}
        itemName={moving?.title}
        current={movingFrom}
        destinations={displayDays.map(d => ({
          id: toLocalDateStr(d),
          label: `${d.toLocaleDateString([], { weekday: "long" })} ${formatTaskDay(d)}`,
          hint: d.toDateString() === today.toDateString() ? "Today" : undefined,
        }))}
        onMove={(d) => {
          const target = displayDays.find(x => toLocalDateStr(x) === d.id);
          if (moving && target) moveTaskToDay(moving, target);
        }}
      />
    </TasksStandalone>
  );
}

export default WeekView;
